"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTheme } from "next-themes";
import { motion, AnimatePresence } from "framer-motion";
import { servicesDropdown, moreDropdown } from "@/data/navigation";

export default function Navbar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const { theme, setTheme } = useTheme();

  useEffect(() => setMounted(true), []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const serviceColors: Record<string, string> = {
    "UI/UX Design": "#3B82F6",
    "Web Development": "#F97316",
    "Graphic Design": "#10B981",
    "Presentation Design": "#8B5CF6",
    "Webflow & Framer": "#0EA5E9",
    "AI & Chatbots": "#E11D24",
  };

  const pageIcons: Record<string, string> = {
    "Home": "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25",
    "About Us": "M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z",
    "Our Team": "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
    "Projects": "M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 002.25-2.25V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z",
    "Contact": "M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75",
  };

  const ThemeToggle = ({ className = "" }: { className?: string }) => (
    <button
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className={`text-foreground/50 hover:text-foreground transition-colors p-3 ${className}`}
      aria-label="Toggle theme"
    >
      {mounted && (theme === "dark" ? (
        <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
        </svg>
      ) : (
        <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
        </svg>
      ))}
    </button>
  );

  return (
    <>
      {/* Overlay when desktop dropdown is open */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          moreOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        onClick={() => setMoreOpen(false)}
      />

      {/* ===== DESKTOP NAVBAR ===== */}
      <nav
        ref={navRef}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 hidden md:block"
      >
        {/* More dropdown — creative redesign */}
        <AnimatePresence>
          {moreOpen && (
            <motion.div
              id="more-menu"
              role="menu"
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-130"
              initial={{ opacity: 0, y: 14, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.96 }}
              transition={{ duration: 0.38, ease: [0.23, 1, 0.32, 1] }}
            >
              <div className="bg-dropdown-bg backdrop-blur-2xl border border-card-border rounded-2xl shadow-2xl shadow-[var(--nav-shadow)] overflow-hidden">
                {/* Top accent line */}
                <motion.div
                  className="h-[1.5px] bg-gradient-to-r from-transparent via-[#E11D24]/70 to-transparent"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.55, delay: 0.1, ease: "easeOut" }}
                />

                <div className="p-2 grid grid-cols-3 gap-0.5">
                  {moreDropdown.map((item, i) => (
                    <motion.div
                      key={item.href}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22, delay: 0.05 + i * 0.04, ease: [0.23, 1, 0.32, 1] }}
                    >
                      <Link
                        href={item.href}
                        className="group flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-150 hover:bg-foreground/5"
                        onClick={() => setMoreOpen(false)}
                      >
                        <span
                          className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-110"
                          style={{ backgroundColor: `${item.color}18` }}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke={item.color} strokeWidth={1.7}>
                            <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                          </svg>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.5px] font-medium text-foreground/75 group-hover:text-foreground transition-colors duration-150 truncate">
                            {item.label}
                          </span>
                          <span className="block text-[10px] text-foreground/30 group-hover:text-foreground/45 transition-colors duration-150 truncate">{item.description}</span>
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop nav pill */}
        <div
          className="relative flex items-center rounded-2xl px-2 lg:px-3 py-2 bg-nav-bg"
          style={{ boxShadow: "rgba(225, 29, 36, 0.4) 0px 3px 4px 0px inset" }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
            <Link href="/projects" className="text-base font-normal text-foreground hover:text-foreground transition-all tracking-[0.32px] px-4 lg:px-6 py-3 block">
              Projects
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
            <Link href="/services" className="text-base font-normal text-foreground hover:text-foreground transition-all tracking-[0.32px] px-4 lg:px-6 py-3 block">
              Services
            </Link>
          </motion.div>
          <motion.span
            className="relative inline-block rounded-lg p-[1.5px] gradient-btn-bg mx-2"
            whileHover={{ scale: 1.04, y: -1 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Link
              href="/contact"
              className="relative z-10 flex items-center gap-2.5 px-6 py-3 rounded-[6px] text-white text-sm font-semibold whitespace-nowrap overflow-hidden group"
              style={{
                background: "var(--nav-cta-gradient)",
              }}
            >
              {/* Subtle red glow on hover */}
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(225,29,36,0.18) 0%, transparent 70%)" }}
              />
              {/* Arrow icon */}
              <span className="relative z-10 w-5 h-5 rounded flex items-center justify-center shrink-0" style={{ background: "#E11D24" }}>
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2 8L8 2M8 2H3M8 2v5" />
                </svg>
              </span>
              <span className="relative z-10 tracking-tight">Start a Project</span>
            </Link>
          </motion.span>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
            <Link href="/pricing" className="text-base font-normal text-foreground hover:text-foreground transition-all tracking-[0.32px] px-4 lg:px-6 py-3 block">
              Pricing
            </Link>
          </motion.div>
          <ThemeToggle />
          <div ref={moreRef}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`text-base font-normal transition-all tracking-[0.32px] px-4 lg:px-6 py-3 ${moreOpen ? "text-foreground" : "text-foreground hover:text-foreground"}`}
              aria-label="More pages menu"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              aria-controls="more-menu"
            >
              More
            </button>
          </div>
        </div>
      </nav>

      {/* ===== MOBILE: FULL-SCREEN MENU OVERLAY ===== */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-50 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-background/95 backdrop-blur-xl" />

            {/* Content */}
            <motion.div
              className="relative z-10 h-full flex flex-col px-6 pt-6 pb-24 overflow-y-auto"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
            >
              {/* Top row: logo + close */}
              <div className="flex items-center justify-between mb-8">
                <Link href="/" onClick={() => setMobileOpen(false)}>
                  <Image src="/assets/devcubix-logo-dark.png" alt="Dev Cubix" width={120} height={48} className="h-8 w-auto object-contain dark:hidden block" />
                  <Image src="/assets/devcubix-logo-white.png" alt="Dev Cubix" width={120} height={48} className="h-8 w-auto object-contain hidden dark:block" />
                </Link>
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="w-11 h-11 rounded-full border border-foreground/10 flex items-center justify-center text-foreground/50 hover:text-foreground hover:border-foreground/20 transition-colors"
                    aria-label="Close menu"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Quick page links — icon chips */}
              <div className="grid grid-cols-3 gap-2 mb-8">
                {moreDropdown.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex flex-col items-center gap-2 py-4 rounded-xl border border-card-border bg-surface-light hover:border-foreground/15 transition-colors"
                  >
                    <svg className="w-5 h-5 text-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={pageIcons[item.label] || pageIcons["Home"]} />
                    </svg>
                    <span className="text-sm font-medium text-foreground/70">{item.label}</span>
                  </Link>
                ))}
              </div>

              {/* Services section */}
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/30 font-semibold mb-3 px-1">Services</p>
                <div className="space-y-1">
                  {servicesDropdown.map((item) => {
                    const color = serviceColors[item.label] || "#E11D24";
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-foreground/[0.04] transition-colors group"
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0 transition-transform group-hover:scale-125" style={{ backgroundColor: color }} />
                        <span className="text-[15px] font-medium text-foreground/80 group-hover:text-foreground transition-colors">{item.label}</span>
                        <svg className="w-4 h-4 ml-auto text-foreground/15 group-hover:text-foreground/40 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Pricing link */}
              <Link
                href="/pricing"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-card-border bg-surface-light hover:border-foreground/15 transition-colors mb-8"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-[15px] font-medium text-foreground/80">View Pricing</span>
                </div>
                <svg className="w-4 h-4 text-foreground/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </Link>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Bottom CTA */}
              <div className="space-y-4">
                <Link
                  href="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-xl text-white font-semibold text-sm"
                  style={{ background: "linear-gradient(135deg, #E11D24, #B91620)" }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z"/>
                  </svg>
                  Start a Project
                </Link>

                {/* Social row */}
                <div className="flex items-center justify-center gap-4 pt-2">
                  {[
                    { name: "Instagram", href: "https://www.instagram.com/devcubix/", icon: "/assets/social/Instagram.svg" },
                    { name: "LinkedIn", href: "https://www.linkedin.com/company/dev-cubix/", icon: "/assets/social/LinkedIn.svg" },
                    { name: "Twitter", href: "https://twitter.com/devcubix", icon: "/assets/social/X-Twitter.svg" },
                  ].map((s) => (
                    <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-foreground/10 flex items-center justify-center hover:border-foreground/20 transition-colors">
                      <Image src={s.icon} alt={s.name} width={16} height={16} className="theme-invert opacity-40" />
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== MOBILE BOTTOM BAR ===== */}
      <nav className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 md:hidden w-[92%] max-w-[400px]">
        <div
          className="relative flex items-center justify-between rounded-2xl px-3 py-2 bg-nav-bg"
          style={{ boxShadow: "rgba(225, 29, 36, 0.4) 0px 3px 4px 0px inset" }}
        >
          {/* Left: Dev Cubix icon */}
          <Link href="/" className="flex items-center gap-1.5 p-2">
            <Image src="/assets/devcubix-logo-dark.png" alt="DC" width={80} height={32} className="h-5 w-auto object-contain dark:hidden block" />
            <Image src="/assets/devcubix-logo-white.png" alt="DC" width={80} height={32} className="h-5 w-auto object-contain hidden dark:block" />
          </Link>

          {/* Center: CTA */}
          <Link
            href="/contact"
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-white text-sm font-bold"
            style={{ backgroundColor: "rgb(225, 29, 36)" }}
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z"/>
            </svg>
            Start a Project
          </Link>

          {/* Right: Menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2.5 rounded-xl text-foreground hover:text-foreground/80 transition-colors"
            aria-label="Menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
      </nav>
    </>
  );
}
